import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Newspaper, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDate } from '@/lib/formatters';

interface RelatedNewsRowProps {
  brandId?: string;
  modelId?: string;
  variantId?: string;
  limit?: number;
  heading?: string;
}

interface NewsItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image_url: string | null;
  published_at: string | null;
}

export function RelatedNewsRow({
  brandId,
  modelId,
  variantId,
  limit = 3,
  heading = 'Læs også fra nyhederne',
}: RelatedNewsRowProps) {
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['related-news', { brandId, modelId, variantId, limit }],
    queryFn: async (): Promise<NewsItem[]> => {
      // 1) Try linked-news via news_post_cars
      const filters: string[] = [];
      if (variantId) filters.push(`variant_id.eq.${variantId}`);
      if (modelId) filters.push(`model_id.eq.${modelId}`);
      if (brandId) filters.push(`brand_id.eq.${brandId}`);

      let linkedIds: string[] = [];
      if (filters.length > 0) {
        const { data: links } = await supabase
          .from('news_post_cars')
          .select('news_post_id')
          .or(filters.join(','))
          .limit(50);
        linkedIds = Array.from(new Set((links || []).map((l: any) => l.news_post_id)));
      }

      const collected: NewsItem[] = [];

      if (linkedIds.length > 0) {
        const { data: linked } = await supabase
          .from('news_posts')
          .select('id, title, slug, excerpt, cover_image_url, published_at')
          .in('id', linkedIds)
          .eq('is_published', true)
          .order('published_at', { ascending: false })
          .limit(limit);
        collected.push(...((linked as NewsItem[]) || []));
      }

      // 2) Fallback: latest published news
      if (collected.length < limit) {
        const need = limit - collected.length;
        const exclude = collected.map((p) => p.id);
        let q = supabase
          .from('news_posts')
          .select('id, title, slug, excerpt, cover_image_url, published_at')
          .eq('is_published', true)
          .order('published_at', { ascending: false })
          .limit(need);
        if (exclude.length > 0) q = q.not('id', 'in', `(${exclude.join(',')})`);
        const { data: fallback } = await q;
        collected.push(...((fallback as NewsItem[]) || []));
      }

      return collected.slice(0, limit);
    },
  });

  if (isLoading || posts.length === 0) return null;

  return (
    <section className="py-10 border-t border-border/60">
      <div className="container mx-auto px-4">
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-display text-lg md:text-xl font-bold flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-primary" />
            {heading}
          </h2>
          <Link
            to="/nyheder"
            className="text-sm text-primary hover:underline inline-flex items-center gap-1"
          >
            Alle nyheder <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {posts.map((post) => (
            <Link
              key={post.id}
              to={`/nyheder/${post.slug}`}
              className="group block bg-card rounded-lg overflow-hidden border border-border/60 hover:border-primary/40 transition-colors"
            >
              <div className="aspect-[16/9] bg-muted overflow-hidden">
                {post.cover_image_url ? (
                  <img
                    src={post.cover_image_url}
                    alt={post.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Newspaper className="w-8 h-8 text-muted-foreground/50" />
                  </div>
                )}
              </div>
              <div className="p-3">
                {post.published_at && (
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                    {formatDate(post.published_at)}
                  </p>
                )}
                <h3 className="font-semibold text-sm leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                  {post.title}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
